import { defu } from 'defu'
import { joinURL, stringifyQuery } from 'ufo'
import { toPublicLocalePath } from '@design-system/utils/publicLocalePath'

/**
 * Sotto @nuxtjs/seo (usa 101): in Unhead, a parità di dedupe key (canonical /
 * og:url), vince il tag con tagPriority più basso. Con 99 i nostri override
 * soppiantano quelli generati da applyDefaults() su route.path interno.
 */
const SEO_TAG_PRIORITY = 99

/**
 * Espone canonical, og:url e il contesto nuxt-schema-org come /en/ invece di
 * /en-wx/. @nuxtjs/seo e nuxt-schema-org:init derivano entrambi da route.path
 * interno (en-wx); senza questi override in SSR l'output resta su en-wx.
 */
export default defineNuxtPlugin({
  name: 'public-intl-seo',
  enforce: 'post',
  order: 1000,
  setup() {
    if (useRuntimeConfig().public.isFederated) return

    const route = useRoute()
    const head = injectHead()
    const siteConfig = useSiteConfig()
    const resolveUrl = createSitePathResolver({
      withBase: true,
      absolute: true,
    })
    const { canonicalQueryWhitelist } = useRuntimeConfig().public['nuxt-seo']

    const requestURL = useRequestURL()

    // route.path resta '/' (seed del router) finché la prima navigazione
    // client non si risolve; il sito non serve pagine sul path radice.
    const isRouteResolved = computed(
      () => Boolean(route.path) && route.path !== '/'
    )

    const publicPath = computed(() => {
      const path = isRouteResolved.value ? route.path : requestURL.pathname
      return toPublicLocalePath(path.startsWith('/') ? path : `/${path}`)
    })

    const canonicalUrl = computed(() => {
      const query = isRouteResolved.value
        ? route.query
        : Object.fromEntries(requestURL.searchParams)
      const url = resolveUrl(publicPath.value).value || publicPath.value
      const filteredQuery = Object.fromEntries(
        Object.entries(query).filter(([key]) =>
          canonicalQueryWhitelist.includes(key)
        )
      )
      return Object.keys(filteredQuery).length
        ? `${url}?${stringifyQuery(filteredQuery)}`
        : url
    })

    head.push({
      templateParams: {
        schemaOrg: computed(() =>
          defu(route.meta?.schemaOrg || {}, siteConfig, {
            url: joinURL(siteConfig.url, publicPath.value),
            path: publicPath.value,
          })
        ),
      },
    })

    useHead(
      {
        link: [
          {
            key: 'canonical',
            rel: 'canonical',
            href: () => canonicalUrl.value,
          },
        ],
      },
      { tagPriority: SEO_TAG_PRIORITY }
    )

    useSeoMeta(
      {
        ogUrl: () => canonicalUrl.value,
      },
      { tagPriority: SEO_TAG_PRIORITY }
    )
  },
})
